import type { JSX } from "preact";
import { useState } from "preact/hooks";
import { objectKeys } from "@gss/common/objectUtil";
import type { Filter, FilterOption } from "@gss/types/measurementStat";
import type { SecurityContext } from "@gss/types/SecurityContext";
import type { LanguageTranslation } from "../../translations/translation.types";
import { Filters } from "../filters/Filters";
import type { FilterSerie, Params } from "../filters/filters.types";
import {
    combineFilterNames,
    combineOptionNames,
    isAllOption,
    toFilterParam,
} from "../filters/FilterUtil";
import type {
    MeasurementStatWithLockedFilters,
    Selected,
    SelectedData,
} from "../types";
import { DistinctColors } from "../util/DistinctColors";
import { multiply } from "../util/multiply";
import { useTranslation } from "../util/Translation";
import { ResultGraphs } from "./ResultGraphs";
import "./ShowResults.css";

const colors = new DistinctColors();

interface RenderData {
    filterSeries: FilterSerie[];
    subtitle: string;
}

interface PartialFilterSerie {
    key: string;
    name: string;
    allSelection: boolean;
    basedOnQuestion: boolean;
    params: Params;
}

interface Props {
    stat: MeasurementStatWithLockedFilters;
    audience: SecurityContext;
    managerOrganistions: string[];
}

export function ShowResultsTab({
    stat,
    audience,
    managerOrganistions,
}: Props): JSX.Element {
    const { translation, translateFilter } = useTranslation();
    const [renderData, setRenderData] = useState<RenderData>();

    const setSelectedData = (data: SelectedData) => {
        const { orgStructure, selected } = data;
        const commonFilters = getCommonFilters(
            translateFilter,
            stat.lockedFilters,
            stat.periods,
            selected,
        );
        const subtitle = getSubtitle(stat.measurement, commonFilters);
        const partialFilterSeries = createFilterSeries(
            translation,
            stat.periods,
            stat.filtersBasedOnQuestion,
            commonFilters,
            selected,
        );
        const keys = partialFilterSeries.map((s) => s.key);
        const colorMap = colors.getMap(keys);
        const filterSeries = partialFilterSeries.map((fs): FilterSerie => ({
            measurement: stat.measurement,
            orgStructure: orgStructure.value,
            color: colorMap[fs.key],
            key: fs.key,
            name: fs.name,
            allSelection: fs.allSelection,
            basedOnQuestion: fs.basedOnQuestion,
            periods: fs.params.periods,
            filters: fs.params.filters,
            orgIds: fs.params.orgIds,
        }));

        setRenderData({ filterSeries, subtitle });

        const hash = createHashQueryParameters(data.selected);
        window.history.replaceState(null, "", hash);
    };

    return (
        <div className="show-results">
            <Filters
                stat={stat}
                audience={audience}
                managerOrganistions={managerOrganistions}
                setSelectedData={setSelectedData}
            />

            {renderData != null && (
                <ResultGraphs
                    stat={stat}
                    questions={stat.questions}
                    subtitle={renderData.subtitle}
                    filterSeries={renderData.filterSeries}
                />
            )}
        </div>
    );
}

function getCommonFilters(
    translateFilter: (name: string) => string,
    lockedFilters: Record<string, FilterOption>,
    defaultPeriod: string,
    selected: Record<string, Selected[]>,
): { name: string; value: string }[] {
    const res: { name: string; value: string }[] = [];
    let addDefaultPeriod = true;
    for (const i of Object.keys(lockedFilters)) {
        const name = lockedFilters[i].name;
        res.push({
            value: i,
            name: translateFilter(name),
        });
    }
    for (const i of Object.keys(selected)) {
        const selections = selected[i];
        if (i === "periods" && selections.length > 0) {
            addDefaultPeriod = false;
        }
        if (selections.length === 1) {
            const s = selections[0];
            if (!isAllOption(s.key)) {
                res.push({
                    value: i,
                    name: translateFilter(s.name),
                });
            }
        }
    }
    if (addDefaultPeriod) {
        res.push({
            value: "periods",
            name: defaultPeriod,
        });
    }
    return res;
}

function createFiltersQueryParameters(
    selectedFilters: Record<string, Selected[]>,
): string {
    const filters = [];
    for (const i of Object.keys(selectedFilters)) {
        const selectedOptions = selectedFilters[i];
        // Skip "all" selection if it's the only one
        if (
            selectedOptions.length === 1 &&
            isAllOption(selectedOptions[0].key)
        ) {
            continue;
        }
        for (const selectedOption of selectedOptions) {
            filters.push(
                `filter=${toFilterParam(i, encodeURIComponent(selectedOption.key))}`,
            );
        }
    }
    return filters.join("&");
}

function createHashQueryParameters(
    selectedFilters: Record<string, Selected[]>,
): string {
    const filtersQueryParameters =
        createFiltersQueryParameters(selectedFilters);
    return `#${filtersQueryParameters}`;
}

function getSubtitle(measurement: string, commonFilters: Filter[]): string {
    const parts = commonFilters
        .filter((f) => f.value !== "periods")
        .map((f) => f.name);
    const periods = commonFilters.find((f) => f.value === "periods");
    if (periods != null) {
        parts.unshift(periods.name);
    }
    return combineFilterNames([measurement, ...parts]);
}

function createFilterSeries(
    t: LanguageTranslation,
    defaultPeriod: string,
    filtersBasedOnQuestion: Set<string>,
    commonFilters: Filter[],
    selected: Record<string, Selected[]>,
): PartialFilterSerie[] {
    const combinations = createFilterCombinations(selected);
    const ignoreFiltersInName = commonFilters.map((f) => f.value);
    return combinations.map((s): PartialFilterSerie => {
        const params = calculateParams(s, defaultPeriod);
        const [name, allSelection] = calculateName(t, s, ignoreFiltersInName);
        const basedOnQuestion = s.some(
            (sel) =>
                filtersBasedOnQuestion.has(sel.filterValue) &&
                !isAllOption(sel.key),
        );
        return {
            key: calculateKey(params),
            params,
            name,
            allSelection,
            basedOnQuestion,
        };
    });
}

interface AugmentedSelected extends Selected {
    filterValue: string;
}

function createFilterCombinations(
    selected: Record<string, Selected[]>,
): AugmentedSelected[][] {
    const selectedEnriched: AugmentedSelected[][] = [];

    for (const i of Object.keys(selected)) {
        const selection = selected[i];
        if (selection.length > 0) {
            selectedEnriched.push(
                selected[i].map((s) => ({
                    ...s,
                    filterValue: i,
                })),
            );
        }
    }

    if (selectedEnriched.length === 0) {
        return [[]];
    }

    let res: AugmentedSelected[][] = [];
    for (const s of selectedEnriched) {
        res = multiply(res, s);
    }

    return res;
}

function calculateKey(params: Params): string {
    const parts: string[] = [];
    for (const i of objectKeys(params).toSorted()) {
        const value = params[i];
        if (value != null) {
            parts.push(`${i}:${value.toSorted().join(",")}`);
        }
    }
    return parts.join("|");
}

function calculateParams(
    selections: AugmentedSelected[],
    defaultPeriod: string,
): Params {
    let orgIds: string[] | undefined;
    let periods: string[] | undefined;
    const filters: string[] = [];
    for (const s of selections) {
        const optionValues = s.options
            .map((o) => o.value)
            .filter((f) => !isAllOption(f));
        if (optionValues.length > 0) {
            switch (s.filterValue) {
                case "organisations":
                    orgIds = optionValues;
                    break;
                case "periods":
                    periods = optionValues;
                    break;
                default:
                    for (const v of optionValues) {
                        filters.push(`${s.filterValue}:${v}`);
                    }
            }
        }
    }
    const res: Params = {
        periods: periods ?? [defaultPeriod],
    };
    if (orgIds != null) {
        res.orgIds = orgIds;
    }
    if (filters.length > 0) {
        res.filters = filters;
    }
    return res;
}

function calculateName(
    t: LanguageTranslation,
    selections: AugmentedSelected[],
    ignoreFilters: string[],
): [string, boolean] {
    const parts: string[] = [];
    let seriesPeriods: string[] = [];
    for (const s of selections) {
        if (ignoreFilters.includes(s.filterValue)) {
            continue;
        }
        if (s.filterValue === "periods") {
            seriesPeriods = s.options.map((o) => o.value);
            continue;
        }
        if (!isAllOption(s.key)) {
            parts.push(s.name);
        }
    }
    if (seriesPeriods.length > 0) {
        parts.unshift(combineOptionNames(seriesPeriods.toSorted()));
    }
    return parts.length > 0
        ? [combineFilterNames(parts), false]
        : [t.filters.all, true];
}
